import type { IChatMessage } from "./chat-model";

export const MAX_CHAT_MESSAGES = 100;

// While the user scrolls up through history we keep a much larger window so
// the live tail is never evicted. Bounds memory for very deep history scrolls.
export const PREPEND_MESSAGE_CAP = 600;

export interface IChatMessagesActions {
  appendMessage(message: IChatMessage): void;

  removeMessage(messageId: string): void;
}

export class ChatMessages
  implements Iterable<IChatMessage>, IChatMessagesActions
{
  protected messagesMap: Record<string, IChatMessage> = {};
  protected pinnedMessages: IChatMessage[] = [];
  public messageIdKeyMap: Record<string, string> = {};

  protected get length() {
    return Object.keys(this.messagesMap).length;
  }

  public size(): number {
    return Object.keys(this.messagesMap).length;
  }

  public findById(messageId: string): IChatMessage | null {
    return this.messagesMap[messageId] ?? null;
  }

  public static fromList(messages: IChatMessage[]): ChatMessages {
    const chatMessages = new ChatMessages();

    for (const message of messages) {
      chatMessages.appendMessage(message);
    }

    return chatMessages;
  }

  public clear(): void {
    this.messagesMap = {};
    this.messageIdKeyMap = {};
  }

  public *[Symbol.iterator](): Iterator<IChatMessage> {
    for (const message of this.sortedMessages()) {
      yield message;
    }
  }

  public *tailIterator(): Iterable<IChatMessage> {
    const sortedMessages = this.sortedMessages();
    for (let i = sortedMessages.length - 1; i >= 0; i--) {
      yield sortedMessages[i]!;
    }
  }

  public head(): IChatMessage | null {
    const sortedMessages = this.sortedMessages();
    return sortedMessages[0] ?? null;
  }

  public tail(tailIndex?: number): IChatMessage | null {
    const sortedMessages = this.sortedMessages();
    return sortedMessages[sortedMessages.length - 1 - (tailIndex ?? 0)] ?? null;
  }

  public pinMessage(messageId: string | null): void {
    const message = messageId ? this.findById(messageId) : null;
    this.pinnedMessages.splice(0, this.pinnedMessages.length);
    if (message) {
      this.pinnedMessages.push(message);
    }
  }

  public getPinnedMessage(): IChatMessage | null {
    return this.pinnedMessages[0] ?? null;
  }

  public appendMessage(message: IChatMessage, originalId?: string) {
    let previous: IChatMessage | null = null;

    if (originalId && this.messagesMap[originalId]) {
      previous = this.messagesMap[originalId] ?? null;
      delete this.messagesMap[originalId];
      delete this.messageIdKeyMap[originalId];
    }

    this.messagesMap[message.id] = message;
    this.messageIdKeyMap[message.id] = originalId ?? message.id;
    this._trimToCapByTimestamp();

    return { previous };
  }

  public replaceMessage(message: IChatMessage, originalId: string) {
    return this.appendMessage(message, originalId);
  }

  public appendMessages(messages: IChatMessage[]) {
    for (const message of messages) {
      this.messagesMap[message.id] = message;
    }
    this._trimToCapByTimestamp();
  }

  public prependMessages(messages: IChatMessage[]) {
    for (const message of messages) {
      this.messagesMap[message.id] = message;
    }
    // History browsing: keep a generous window and only ever trim the OLDEST
    // (messages the user scrolled away from upward). Never trim the newest,
    // which would silently and permanently evict the live tail with no
    // recovery on scroll-down. Memory stays bounded by PREPEND_MESSAGE_CAP.
    this._trimOldestToCap(PREPEND_MESSAGE_CAP);
  }

  private _trimToCapByTimestamp(): void {
    this._trimOldestToCap(MAX_CHAT_MESSAGES);
  }

  private _trimOldestToCap(cap: number): void {
    if (this.size() <= cap) return;
    const sorted = Object.values(this.messagesMap).sort((a, b) =>
      a.timestamp.localeCompare(b.timestamp),
    );
    const overflow = sorted.length - cap;
    for (let i = 0; i < overflow; i++) {
      const id = sorted[i]!.id;
      delete this.messagesMap[id];
      delete this.messageIdKeyMap[id];
    }
  }

  public addMessageTranslation(messageId: string, translation: string): void {
    const message = this.findById(messageId);

    if (!message) return;

    this.appendMessage({
      ...message,
      messageTranslation: translation,
    });
  }

  public addReplyToTranslation(
    quotedMessageId: string,
    translation: string,
  ): void {
    for (const id of Object.keys(this.messagesMap)) {
      const msg = this.messagesMap[id]!;
      if (msg.replyTo?.id !== quotedMessageId) continue;
      this.messagesMap[id] = {
        ...msg,
        replyTo: { ...msg.replyTo!, messageTranslation: translation },
      };
    }
  }

  public removeMessage(messageId: string): void {
    if (this.messagesMap[messageId]) {
      delete this.messagesMap[messageId];
      delete this.messageIdKeyMap[messageId];
    }
  }

  private sortedMessages(): IChatMessage[] {
    return Object.values(this.messagesMap).sort((a, b) =>
      a.timestamp.localeCompare(b.timestamp),
    );
  }
}
